import { Dialog } from '@mui/material';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import queryString from 'query-string';
import React, { createContext, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import db_config from '../../configs/db_config';
import { BillStatus, OrderItemStatus } from '../../consts';
import useCreateOrder from '../../hook/useCreateOrder';
import useGetListUsers from '../../hook/useGetListUsers';
import { CreateOrderDTO, FoodItem, Order, User_Rider } from '../../interfaces/types';
import { db } from '../../libs/firebase';
import { generateOrderId } from '../../utils';
import { useSystemContext } from '../SystemContext/SystemContext';

interface NewOrderContextType {
  updateOrderDetails: (details: CreateOrderDTO) => void;
  updateOrderId: (id: string) => Promise<string>;
  createOrder: (orderDetails: CreateOrderDTO, orderId: string) => Promise<boolean>;
  orderDetails: CreateOrderDTO;
  setOrderDetails: Dispatch<SetStateAction<CreateOrderDTO>>;
  loadingOrder: boolean;
  orders: Order[];
  orderId: string | undefined;
  errorOrder: any;
  setIsCreate: Dispatch<SetStateAction<boolean>>;
}

const NewOrderContext = createContext<NewOrderContextType>({
  updateOrderDetails: () => {},
  updateOrderId: async (id: string) => {
    return '';
  },
  orderDetails: {
    order_status: OrderItemStatus.PREPARING,
    order_table_number: '',
    foods: [] as FoodItem[],
    promotion_foods: [] as FoodItem[],
    bill_status: BillStatus.NOT_CLEARED,
    payment_method: '',
    stadium_id: '',
    seat_type: '',
    seat_number: '',
  },
  setOrderDetails: () => {},
  loadingOrder: false,
  orders: [],
  orderId: undefined,
  errorOrder: false,
  createOrder: async () => {
    return false;
  },
  setIsCreate: () => {},
});

export const useNewOrderContext = () => React.useContext(NewOrderContext);

const PRE_ORDER_DELIVERY_REQUEST_KEY = 'pre_order_delivery_request';

const getSavedPreOrderDeliveryRequest = () => {
  if (typeof window === 'undefined') return '';
  return sessionStorage.getItem(PRE_ORDER_DELIVERY_REQUEST_KEY) || '';
};

export const NewOrderProvider: React.FC = ({ children }) => {
  const [riders, setRiders] = useState<{ [id: string]: User_Rider }>({});
  const [loadingRiders, setLoadingRiders] = useState(true);
  const [orders, setOrders] = useState<Order[]>([]);
  const { t } = useTranslation();
  const [orderDetails, setOrderDetails] = useState<CreateOrderDTO>({
    order_status: OrderItemStatus.PREPARING,
    order_table_number: '',
    foods: [] as FoodItem[],
    promotion_foods: [] as FoodItem[],
    bill_status: BillStatus.NOT_CLEARED,
    payment_method: '',
    stadium_id: '',
    seat_type: '',
    seat_number: '',
    delivery_request: getSavedPreOrderDeliveryRequest(),
  });
  const [orderId, setOrderId] = useState<string>();
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [isCreate, setIsCreate] = React.useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const { seatInfo, isLoadingSeat, isLoadingStadium, stadiumInfo } = useSystemContext();
  const { data: listUsers } = useGetListUsers();

  const handleCloseDialog = async () => {
    localStorage.removeItem('received_order');
    const currentOrderId = sessionStorage.getItem('orderId');

    if ((!currentOrderId || orders.findIndex((o) => o.id === currentOrderId) > -1) && seatInfo?.id) {
      await updateOrderId();
    }
    const { tab, ...cleanedQueryParams } = queryParams;
    const neworderDetailLink = queryString.stringifyUrl({
      url: `/neworder`,
      query: { ...cleanedQueryParams },
    });
    navigate(neworderDetailLink);

    setOpenDialog(false);
  };

  const { createOrder: rawCreateOrder, errorOrder } = useCreateOrder();

  const createOrder = async (orderDetails: CreateOrderDTO, orderId: string) => {
    return rawCreateOrder(orderDetails, orderId, false);
  };

  const updateOrderDetails = (details: CreateOrderDTO) => {
    if (details.delivery_request) {
      sessionStorage.setItem(PRE_ORDER_DELIVERY_REQUEST_KEY, details.delivery_request);
    } else {
      sessionStorage.removeItem(PRE_ORDER_DELIVERY_REQUEST_KEY);
    }
    setOrderDetails(details);
  };

  const updateOrderId = async () => {
    const orderNewId = await generateOrderId(seatInfo!, stadiumInfo?.id);
    setOrderId(orderNewId);
    sessionStorage.setItem('orderId', orderNewId);
    return orderNewId;
  };

  const colRef = db.collection(db_config.DB_TABLE_ALL_ORDERS);
  const restaurantNameCache = useRef<{ [id: string]: { name: string | null; profile_pic: string | null } }>({});

  const getAllRider = async () => {
    if (!listUsers) {
      setLoadingRiders(false);
      return;
    }
    try {
      const users = await db.collection(db_config.DB_TABLE_STADIUM_MANAGE_USERS).where('role', '==', 'RIDER').get();
      const configSnapshot = await db.collection(db_config.DB_TABLE_STADIUM_CONFIGURATION).doc(db_config.DB_TABLE_STADIUM_CONFIGURATION).get();
      const configData = configSnapshot.data();
      const data: any = {};
      users.docs.forEach((user) => {
        const rider = user.data();
        if (rider?.user_name) {
          const userInfo = listUsers.find((u: any) => u.uid === user.id);
          const gender = userInfo?.gender ?? 0;
          const imageUrl = configData?.rider_url?.[gender] ?? null;
          const riderInfor = {
            name: rider.user_name,
            image: imageUrl,
          };
          data[user.id] = riderInfor;
        }
      });

      setRiders(data);
    } catch (error) {
      console.error('Error stadium_manage_users: ', error);
    } finally {
      setLoadingRiders(false);
    }
  };

  const getDistinctRestaurantIds = (ordersArr: any[]) => {
    const s = new Set<string>();
    ordersArr.forEach((o) => {
      if (Array.isArray(o.restaurant_ids) && o.restaurant_ids.length) {
        o.restaurant_ids.forEach((id: string) => {
          if (id) s.add(id);
        });
        return;
      }
    });
    return Array.from(s);
  };

  const fetchRestaurantNames = async (ids: string[]) => {
    if (!ids.length) return;
    const chunkSize = 10;
    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize);
      try {
        const snapshots = await db.collection(db_config.DB_TABLE_RESTAURANT_CONFIG).where(firebase.firestore.FieldPath.documentId(), 'in', chunk).get();

        snapshots.forEach((d) => {
          const data = d.data();
          restaurantNameCache.current[d.id] = {
            name: data?.name ?? null,
            profile_pic: data?.restaurant_profile_pic ?? null,
          };
        });
      } catch (err) {
        const results = await Promise.all(
          chunk.map((id) =>
            db
              .collection(db_config.DB_TABLE_RESTAURANT_CONFIG)
              .doc(id)
              .get()
              .then((d) => ({ id, data: d.exists ? d.data() : null }))
          )
        );
        results.forEach((r) => {
          restaurantNameCache.current[r.id] = {
            name: r.data?.name ?? null,
            profile_pic: r.data?.restaurant_profile_pic ?? null,
          };
        });
      }
    }
  };

  const enrichOrdersWithNames = (ordersArr: any[]) =>
    ordersArr.map((o: any) => ({
      ...o,
      foods: Array.isArray(o.foods)
        ? o.foods.map((f: any) => ({
            ...f,
            restaurant_name: restaurantNameCache.current[f.restaurantId]?.name ?? null,
            restaurant_profile_pic: restaurantNameCache.current[f.restaurantId]?.profile_pic ?? null,
          }))
        : o.foods,
    }));

  const getOrderHistory = async () => {
    if (location.pathname.includes('download-ticket')) {
      return;
    }

    if (isLoadingSeat || isLoadingStadium) return;

    if (seatInfo?.id && !isCreate) {
      await updateOrderId();
    }

    try {
      setLoadingOrder(true);
      const contactEmail = seatInfo?.email || '';

      if (!seatInfo?.seat_number || !contactEmail) {
        setLoadingOrder(false);
        return;
      }

      const listenersToUse: firebase.firestore.Query<firebase.firestore.DocumentData>[] = [];
      if (contactEmail) {
        listenersToUse.push(
          colRef.where('order_table_number', '==', seatInfo.id).where('email', '==', contactEmail).orderBy('order_create_time', 'desc'),
          colRef.where('order_table_number', '==', seatInfo.id).where('gift_info.receiver_email', '==', contactEmail).orderBy('order_create_time', 'desc')
        );
      }

      const refreshCombinedOrders = async () => {
        try {
          const snaps = await Promise.all(listenersToUse.map((q) => q.get()));
          const docsMap = new Map<string, any>();

          snaps.forEach((snap) => {
            snap.forEach((doc) => {
              const data = doc.data();
              if (data?.is_damaged === true) return;
              let rider = riders[data?.rider_id] ?? null;
              docsMap.set(doc.id, { ...data, id: doc.id, rider });
            });
          });

          const orderHistory = Array.from(docsMap.values()).map((order: any) => ({
            ...order,
          }));

          setOrders(orderHistory);

          const missing = getDistinctRestaurantIds(orderHistory).filter((id) => !restaurantNameCache.current[id]);
          if (missing.length) {
            try {
              await fetchRestaurantNames(missing);
              setOrders((prev: any[]) => enrichOrdersWithNames(prev));
            } catch (err) {
              console.error('Error fetching restaurant configs:', err);
            }
          } else {
            setOrders((prev: any[]) => enrichOrdersWithNames(prev));
          }
        } catch (err) {
          console.error('Error combining order queries:', err);
        } finally {
          setLoadingOrder(false);
        }
      };

      const unsubscribes = listenersToUse.map((q) =>
        q.onSnapshot((snapshot) => {
          try {
            snapshot.docChanges().forEach((_change) => {
              if (_change.type === 'modified') {
                const data = _change.doc.data();
                if (data?.order_status === OrderItemStatus.DELIVERED) {
                  const orderId = _change.doc.id;

                  const deliveredOrdersJson = localStorage.getItem('delivered_orders');
                  const deliveredOrders: string[] = deliveredOrdersJson ? JSON.parse(deliveredOrdersJson) : [];

                  const isOnDownloadTicketPage = location.pathname.includes('download-ticket');
                  if (!deliveredOrders.includes(orderId) && !isOnDownloadTicketPage) {
                    deliveredOrders.push(orderId);
                    localStorage.setItem('delivered_orders', JSON.stringify(deliveredOrders));
                    setOpenDialog(true);
                  }
                }
              }
            });
          } catch (err) {}

          refreshCombinedOrders();
        })
      );

      await refreshCombinedOrders();

      return unsubscribes;
    } catch (error) {
      setLoadingOrder(false);
      console.error('error:', error);
    }
  };

  useEffect(() => {
    getAllRider();
  }, [listUsers]);

  useEffect(() => {
    const fetchOrderHistory = async () => {
      const user_uuid = localStorage.getItem('uuid');

      if (!user_uuid) {
        let retries = 3;
        while (retries > 0) {
          const uuid = uuidv4();
          localStorage.setItem('uuid', uuid);

          const storedUuid = localStorage.getItem('uuid');
          if (storedUuid === uuid) {
            await getOrderHistory();
            break;
          } else {
            retries--;
            console.warn(`Retry storing uuid (${3 - retries}/3)`);
          }
        }
      } else {
        await getOrderHistory();
      }
    };

    if (!loadingRiders) {
      fetchOrderHistory();
    }

    return () => {};
  }, [seatInfo, isLoadingSeat, isLoadingStadium, loadingRiders]);

  return (
    <NewOrderContext.Provider
      value={{
        updateOrderDetails,
        createOrder,
        updateOrderId,
        orderDetails,
        setOrderDetails,
        loadingOrder,
        orders,
        errorOrder,
        orderId,
        setIsCreate,
      }}
    >
      {children}
      <Dialog open={openDialog} sx={{ '& .MuiPaper-root': { borderRadius: '12px', width: '100%' } }}>
        <div className="p-6 flex flex-col gap-6 w-full items-center">
          <p className="text-black-900 text-lg font-semibold leading-7 text-center">{t('RECEIVED_ORDER')}</p>
          <button
            className={`px-18 py-2.5 rounded-lg border border-solid shadow-button text-base font-semibold leading-6 bg-primary-green border-primary-green text-white`}
            onClick={handleCloseDialog}
          >
            {t('RECEIVED_ORDER_BUTTON')}
          </button>
        </div>
      </Dialog>
    </NewOrderContext.Provider>
  );
};
